import { navigationV2 } from "@src/shared/navigation/v2";
import { is404Error } from "@src/shared/utils/http/errors";
import { captureException } from "@src/shared/utils/observability/sentry";

import { authClient } from "../../data";

export type ResolvePostAuthDestinationV2Options = {
  route?: string;
  operation?: string;
  redirectTo?: string | null;
  fallback?: string;
};

const SAFE_V2_REDIRECT_PATHS = [
  "/documents",
  "/home",
  "/not-found",
  "/patients",
  "/settings",
  "/sign-up",
  "/visit",
  "/visits",
];

function isSafeV2Redirect(redirectTo: string): boolean {
  if (!redirectTo.startsWith("/")) return false;
  if (redirectTo.startsWith("//")) return false;

  try {
    const url = new URL(redirectTo, window.location.origin);

    if (url.origin !== window.location.origin) return false;

    return (
      url.pathname === "/" ||
      SAFE_V2_REDIRECT_PATHS.some(
        (path) => url.pathname === path || url.pathname.startsWith(`${path}/`),
      )
    );
  } catch {
    return false;
  }
}

function getSuccessDestination({
  fallback = navigationV2.home(),
  redirectTo,
}: ResolvePostAuthDestinationV2Options): string {
  if (redirectTo && isSafeV2Redirect(redirectTo)) return redirectTo;

  return fallback;
}

export async function resolvePostAuthDestinationV2(
  options: ResolvePostAuthDestinationV2Options = {},
): Promise<string> {
  const successDestination = getSuccessDestination(options);

  try {
    const response = await authClient.$fetch<{
      state: string;
    }>("/sign-up/stitch/status", { method: "GET" });

    const state = response.data?.state;

    if (state === "pending") {
      return navigationV2.signUp(4);
    }

    return successDestination;
  } catch (error) {
    if (is404Error(error)) {
      return successDestination;
    }

    const statusCode = (error as { status?: number }).status;
    if (statusCode === 401) {
      return successDestination;
    }

    const route = options.route ?? "v2/auth";
    const operation = options.operation ?? "resolvePostAuthDestinationV2";

    try {
      captureException(error, {
        tags: { route, operation },
      });
    } catch {
      // ignore sentry failures
    }

    return successDestination;
  }
}
